import React from "react";
import { StyleRoot } from "radium";
import { Link } from "react-router";
import Helmet from "react-helmet";
import assign from "lodash/assign";
import moment from "moment-timezone";
import Select from "react-select";
import {
  Dialog,
  EnhancedButton,
  AppBar,
  Menu,
  IconButton,
  RaisedButton,
  FlatButton,
  Snackbar,
  CircularProgress
} from "material-ui";
import { spacing as Spacing, colors as Colors } from "material-ui/styles";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import { StripeProvider } from "react-stripe-elements";
import withWidth, { SMALL, MEDIUM, LARGE } from "material-ui/utils/withWidth";
import { lighten } from "material-ui/utils/colorManipulator";
import ReactGA from "react-ga4";
import config from "../config";
import { hasMobileUA } from "../util";
import { isApp, checkDevice } from "../util";
import NavigationMenu from "material-ui/svg-icons/navigation/menu";

import {
  updateSavedScrollForLocation,
  SCROLL_UPDATE_INTERVAL
} from "../app-root.jsx";

import theme from "../theme";
import AppLeftNav from "./app-left-nav.jsx";
import FullWidthSection from "./full-width-section.jsx";
import AccountAppBarItem from "./partials/account-app-bar-item.jsx";
import AdminCommunicator from "./partials/admin-communicator.jsx";
import Banner from "./partials/banner.jsx";
import LinkLike from "./partials/link-like.jsx";
import InjectedTenantScripts from "./partials/injected-tenant-scripts.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import PersonStore from "../stores/PersonStore";
import PersonActions from "../actions/PersonActions";
import AppStore from "../stores/AppStore";
import AppActions from "../actions/AppActions";
import GlobalNotificationStore from "../stores/GlobalNotificationStore";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

const PassThroughComponent = ({ children }) => <div>{children}</div>;

// Denver Film, Sun Valley, New Orleans, App Demo Organization, and Chris' local demo org ,"62bb22669b577100f26955a7"
const SWITCHER_ORGS = ["5eb1e2d3dd5658002f70a5dc","61a7e49f3c4f6800627cd4a4","5ff368c6a800ce7dce62dba0","5d67feada2ae7200297885ba"];
//const SWITCHER_ORGS = ["62bb22669b577100f26955a7","5ff368c6a800ce7dce62dba0"];

@connectToStores
class App extends React.Component {
  constructor() {
    super();

    this.state = {
      tenant: TENANT, // Initially hydrate the tenant using the TENANT hardcoded in JS
      isEmbed: false,
      mobileEmbedWantsToProceed: false,
      showDownloadAppModal: false,
      selectedEventBucket: null,
      eventBucketOptions: [],
      hasSwitcher: false
    };

    this.updateTenant = this.updateTenant.bind(this);
    this.switchEventBucket = this.switchEventBucket.bind(this);

    this.getStyles = this.getStyles.bind(this);
    this._getTheme = this._getTheme.bind(this);
    this._onLeftIconButtonClick = this._onLeftIconButtonClick.bind(this);
  }

  static getStores() {
    return [AppStore, PersonStore, GlobalNotificationStore];
  }

  static getPropsFromStores() {
    let state = AppStore.getState();
    state.user = PersonStore.getState();
    state.globalNotification = GlobalNotificationStore.getState();
    return state;
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  static get childContextTypes() {
    return {
      muiTheme: React.PropTypes.object,
      router: React.PropTypes.object,
      tenant: React.PropTypes.object,
      appSettings: React.PropTypes.object,
      isLarge: React.PropTypes.bool,
      isMedium: React.PropTypes.bool,
      isSmall: React.PropTypes.bool,
      isEmbed: React.PropTypes.bool
    };
  }

  getChildContext() {
    let isLarge = this.props.width === LARGE;

    // When embedded, allow smaller widths to still be considered "large"
    if (this.state.isEmbed) {
      isLarge = isLarge || window.innerWidth > 600;
    }

    return {
      router: this.context.router,
      tenant: this.state.tenant,
      appSettings: this.props.settings || {},
      isEmbed: this.state.isEmbed,
      isLarge: isLarge,
      isMedium: this.props.width === MEDIUM,
      isSmall: this.props.width === SMALL
    };
  }

  componentWillMount() {
    this.componentWillReceiveProps(this.props);
    AppActions.fetchSettings();
    AppActions.fetchEventBuckets.defer();
    if (
      process.env.NODE_ENV === "production" &&
      process.env.GOOGLE_ANALYTICS_ID
    ) {
      ReactGA.initialize(process.env.GOOGLE_ANALYTICS_ID);
      ReactGA.send("pageview");

      this.unlisten = this.props.router.listen(() => {
        ReactGA.send("pageview");
      });
    }
  }

  componentWillUnmount() {
    if (!this.unlisten) return;

    this.unlisten();
  }

  componentDidMount() {
    const hasSwitcher = document.getElementById("event_bucket_switcher");
    this.setState({ hasSwitcher: hasSwitcher });
    if (window && window.CookieConsent && !hasSwitcher) { 
      window.CookieConsent.run({
        categories: {
          necessary: {
            enabled: true,  // this category is enabled by default
            readOnly: true  // this category cannot be disabled
          },
          analytics: {},
          advertising: {},
        },
        language: {
          default: 'en',
          translations: {
            en: {
              consentModal: {
                title: 'Manage cookie preferences',
                description: 'We use cookies to improve your experience. Some cookies are necessary for our website and services to function properly. Other cookies are optional and help personalize your experience, including advertising and analytics. You can consent to all cookies, decline all optional cookies, or manage optional cookies. Without a selection, our default cookie settings will apply.',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                showPreferencesBtn: 'Manage Individual preferences'
              },
              preferencesModal: {
                title: 'Manage cookie preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                savePreferencesBtn: 'Accept current selection',
                closeIconLabel: 'Close modal',
                sections: [
                  {
                      title: '',
                      description: 'We use cookies to improve your experience. Some cookies are necessary for our website and services to function properly. Other cookies are optional and help personalize your experience, including advertising and analytics. You can consent to all cookies, decline all optional cookies, or manage optional cookies. Without a selection, our default cookie settings will apply.'
                  },
                  {
                      title: 'Strictly Necessary cookies',
                      description: 'These cookies are essential for the proper functioning of the website and cannot be disabled.',

                      //this field will generate a toggle linked to the 'necessary' category
                      linkedCategory: 'necessary'
                  },
                  {
                      title: 'Advertising',
                      description: 'These cookies are used to make advertising messages more relevant to users. They perform functions like preventing the same ad from continuously reappearing, ensuring that ads are properly displayed for advertisers, and in some cases selecting advertisements that are based on user interests.',
                      linkedCategory: 'advertising'
                  },
                  {
                      title: 'Performance and Analytics',
                      description: 'These cookies collect information about how you use our website and to help us customize the website for you.',
                      linkedCategory: 'analytics'
                  },
                  {
                      title: 'More information',
                      description: 'For any queries in relation to my policy on cookies and your choices, please see our <a href="https://eventive.org/privacy" target="_blank">privacy policy</a>'
                  }
                ]
              }
            }
          }
        }
      });
    } else {

    }
    if (window.ga) {
      window.ga("set", "&uid", this.props.user.id);
    }
    const hasAppLinkQueryParam =
      this.context.router.location.query.applink != undefined;
    const hasAppStoreConfig =
      this.state.tenant.apple_app_store_url &&
      this.state.tenant.google_play_store_url;
    if (
      checkDevice().isMobile &&
      !isApp() &&
      hasAppLinkQueryParam &&
      hasAppStoreConfig
    ) {
      this.setState({ showDownloadAppModal: true });
    }
  }

  componentWillReceiveProps(props) {
    // No longer show the embed intermediary screen, but keep a query param to allow the option to do so
    if (props.location.query.embed) {
      let forceShowEmbedOnMobile = true;
      if (props.location.search && props.location.search.indexOf("allow_mobile_embed=0") > 0) {
        forceShowEmbedOnMobile = false;
      }
      this.setState({
        isEmbed: true,
        embedBasePath:
          props.location.pathname +
          props.location.search.replace(/[\&\?]embed[^\&]+/g, ""),
        mobileEmbedWantsToProceed:
          forceShowEmbedOnMobile ||
          (!this.state.mobileEmbedWantsToProceed &&
            props.location.pathname !== "/schedule" &&
            props.location.pathname !== "/films")
      });

      // Scroll position tracking
      if (!this.scrollInterval) {
        this.scrollInterval = setInterval(() => {
          updateSavedScrollForLocation(this.props.location);
        }, SCROLL_UPDATE_INTERVAL);
      }
    }

    // Additional url specific settings - persist to tenant config
    if (props.location.query.hide_search) {
      let { tenant } = this.state;
      tenant.film_guide_hide_search = true;
      this.setState({ tenant });
    }

    // Global for user-injected scripts
    window.PERSON_ID = (props.user && props.user.id) || "<unknown id>";
    window.PERSON_EMAIL =
      (props.user && props.user.details && props.user.details.email) ||
      "<unknown email>";

    // Sundance class
    if (this.state.tenant && this.state.tenant.sundance_login) {
      if (!document.body.classList.contains("Eventive--Sundance")) {
        document.body.classList.add("Eventive--Sundance");
      }
    }

    //only do this for SWITCHER ORGS

    if (SWITCHER_ORGS.includes(this.state.tenant.owner)) {
      if (AppStore.getState().userEventBuckets && AppStore.getState().userEventBuckets.length > 0) {
        let currentEventBucket = "";
        let newOptions = AppStore.getState().userEventBuckets.map(e => {
          if (this.state.tenant.event_bucket === e.id) {
            currentEventBucket = e.frontend_url;
            console.log("Selected: " + e.frontend_url);
          }
          return { value: e.frontend_url, label: e.name };
        });
        this.setState({ eventBucketOptions: newOptions, selectedEventBucket: currentEventBucket });
      }
    }
  }

  _getTheme() {
    let { tenant } = this.state;

    // For embeds - force text color
    if (this.props.location.query.text_color) {
      tenant.palette.textColor = this.props.location.query.text_color;
      tenant.embedded_text_color = this.props.location.query.text_color;
    }
    if (this.props.location.query.embed_background_color) {
      tenant.embedded_background_color =
        this.props.location.query.embed_background_color;
    }

    return theme.getThemeForTenant(tenant);
  }

  _onLeftIconButtonClick() {
    this.refs.leftNav.toggle();
  }

  updateTenant(tenant) {
    this.setState({ tenant });
  }

  switchEventBucket(bucket) {
    if (!bucket) { return; }
    let newPath = `https://${bucket.value}.eventive.org`;
    // Avoid additional set of "ebs=1&no_cc=1" parameters to URL with every switch
    if (window.location.search !== "" && window.location.search.indexOf("ebs=1&no_cc=1") === -1) {
      newPath = newPath + window.location.search + "&ebs=1&no_cc=1";
    }
    else if (window.location.search !== "") {
      newPath = newPath + window.location.search;
    }
    else {
      newPath = newPath + "?ebs=1&no_cc=1";
    }
    window.location.href = newPath;
  }

  getStyles(theme) {
    return {
      canvas: {
        height: "100vh",
        WebkitFontSmoothing: "antialiased",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        color: theme.palette.textColor
      },
      switcher: {
        display: SWITCHER_ORGS.includes(this.state.tenant.owner) ? "block" : "none",
        position: "fixed",
        top: SWITCHER_ORGS.includes(this.state.tenant.owner) ? "0px" : "-60px",
        flex: "none",
        width: "100%",
        height: "35px",
        backgroundColor: "black",
        color: "white",
        paddingTop: 10,
        paddingBottom: 10,
        zIndex: 1200
      },
      bar: {
        position: "fixed",
        top: SWITCHER_ORGS.includes(this.state.tenant.owner) ? "55px" : "0px",
        flex: "none",
        borderBottom:
          this.state.tenant && this.state.tenant.invert_colors_alt
            ? "1px solid #0000000d"
            : undefined
      },
      title: {
        display: "flex",
        alignItems: "center"
      },
      titleImage: {
        height: 40
      },
      footer: {
        backgroundColor: Colors.grey900,
        textAlign: "center",
        color: Colors.white,
        flex: "none",
        marginTop: 20
      },
      footerWhenLarge: {
        marginTop: 0
      },
      img: {
        height: "80px"
      },
      wrapper: {
        paddingTop: SWITCHER_ORGS.includes(this.state.tenant.owner) ? 94 : 64,
        flex: "1 0 auto"
      },
      wrapperNoHeader: {
        paddingTop: SWITCHER_ORGS.includes(this.state.tenant.owner) ? 55 : 0,
        flex: "1 0 auto"
      },
      wrapperWhenLarge: {},
      bannerWrapper: {
        display: "block",
        textAlign: "center"
      },
      content: {
        maxWidth: 960,
        margin: "0 auto",
        width: "100%"
      },
      contentWhenLarge: {
        padding: `0 10px 50px`
      },
      poweredBy: {
        fontStyle: "italic"
      },
      footerLink: {
        textDecoration: "none",
        color: Colors.white
      },
      eventiveLogo: {
        width: 75,
        marginLeft: 2,
        verticalAlign: -1,
        filter: theme.palette.textColor === "#fff" ? "invert(100%)" : undefined
      },
      dialog: {
        logoContainer: {
          textAlign: "center"
        },
        logo: {
          width: 100
        },
        actions: {
          textAlign: "center"
        }
      },
      embedFooter: {
        marginTop: 30,
        textAlign: "center",
        color: lighten(theme.palette.textColor, 0.1)
      },
      embedFooterLink: {
        color: lighten(theme.palette.textColor, 0.1),
        textDecoration: "none"
      },
      embedInnerContent: {
        padding: "0 10px",
        color: theme.palette.textColor
      }
    };
  }

  render() {
    const muiTheme = getMuiTheme(this._getTheme());
    var iconElementRight,
      styles = this.getStyles(muiTheme),
      path = this.props.routes,
      { isLarge } = this.getChildContext();

    let _StripeProvider = StripeProvider;
    if (!window.Stripe) {
      // Fallback if no Stripe connection
      _StripeProvider = PassThroughComponent;
    }

    // EMBED mode
    if (this.state.isEmbed) {
      let descriptor =
          this.state.embedBasePath.indexOf("/schedule") !== -1
            ? "schedule"
            : this.state.embedBasePath.indexOf("/passes/buy") !== -1
            ? "pass sales"
            : this.state.embedBasePath.indexOf("/films") !== -1
            ? (this.state.tenant && this.state.tenant.display_film_guide) ||
              "film guide"
            : "site",
        embedLabel = `Launch full ${descriptor}`;

      return (
        <_StripeProvider apiKey={process.env.STRIPE_PUBLIC_KEY}>
          <MuiThemeProvider muiTheme={muiTheme}>
            <StyleRoot>
              <div className={isApp() ? "eventive-app" : null}>
                {this.props.fetchSuccessful ? (
                  <div>
                    {SWITCHER_ORGS.includes(this.state.tenant.owner) ? (
                      <div id="event_bucket_switcher" style={styles.switcher}>
                        <div style={{ paddingRight: 10, width: 260, float: "right" }}>
                          <Select
                            name="test"
                            style={{ zIndex: 5000, width: 250, height: 20 }}
                            value={this.state.selectedEventBucket}
                            options={this.state.eventBucketOptions}
                            menuContainerStyle={{ zIndex: 5000, width: 250 }}
                            menuStyle={{ width: 250 }}
                            clearable={false}
                            onChange={this.switchEventBucket}
                          />
                        </div>
                      </div>
                    ) : null}
                    <AppBar
                      title={
                        <Link
                          className="displayFlex"
                          style={{ color: "inherit", textDecoration: "none" }}
                          to="/"
                          target="_blank"
                        >
                          {this.state.tenant.logo_image ? (
                            <img
                              style={styles.titleImage}
                              src={this.state.tenant.logo_image}
                            />
                          ) : (
                            this.state.tenant.display_short_name
                          )}
                        </Link>
                      }
                      titleStyle={styles.title}
                      iconStyleRight={{
                        marginTop: 10,
                        marginLeft: 0,
                        marginRight: 0
                      }}
                      showMenuIconButton={false}
                      iconElementRight={
                        <AccountAppBarItem
                          compact={!isLarge}
                          openInNewTab={true}
                          style={styles.embedAccountAppBarItem}
                        />
                      }
                      zDepth={0}
                    />
                    <div style={styles.embedInnerContent}>
                      {this.state.isEmbed &&
                      (!isLarge || hasMobileUA()) &&
                      !this.state.mobileEmbedWantsToProceed ? (
                        <div
                          style={{
                            textAlign: "center",
                            marginTop: 50,
                            minHeight: 130
                          }}
                        >
                          <div>Looks like you're on a mobile device.</div>
                          <RaisedButton
                            primary={true}
                            label={embedLabel}
                            href={this.state.embedBasePath}
                            target="_blank"
                          />
                          <div style={{ marginTop: 20 }}>
                            Or,{" "}
                            <LinkLike
                              onClick={() =>
                                this.setState({
                                  mobileEmbedWantsToProceed: true
                                })
                              }
                            >
                              view embedded {descriptor}
                            </LinkLike>
                            .
                          </div>
                        </div>
                      ) : (
                        React.cloneElement(this.props.children, {
                          appSettings: this.props.settings || {},
                          previewMode: this.props.isPreviewMode
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 10, textAlign: "center" }}>
                    <CircularProgress
                      mode="indeterminate"
                      size={119}
                      thickness={7}
                      style={{ margin: 10.5 }}
                    />
                  </div>
                )}
                <div style={styles.embedFooter} data-iframe-height="true" className="Eventive--Footer">
                  <a
                    style={styles.embedFooterLink}
                    href="https://eventive.org"
                    target="_blank"
                  >
                    Powered by{" "}
                    <img
                      style={styles.eventiveLogo}
                      src="/img/eventive-dark.png"
                    />
                  </a>
                  <div>
                    <Link style={styles.embedFooterLink} to="/" target="_blank">
                      View full site
                    </Link>
                  </div>
                </div>
                {this.state.tenant.additional_scripts ? (
                  <InjectedTenantScripts
                    content={this.state.tenant.additional_scripts}
                  />
                ) : null}
              </div>
            </StyleRoot>
          </MuiThemeProvider>
        </_StripeProvider>
      );
    }

    if (isLarge) {
      styles.wrapper = assign({}, styles.wrapper, styles.wrapperWhenLarge);
      styles.content = assign({}, styles.content, styles.contentWhenLarge);
      styles.footer = assign({}, styles.footer, styles.footerWhenLarge);
    }

    // Legacy support for old banner format
    if (!this.state.tenant.banner_image) {
      this.state.tenant.banner_image =
        this.props.settings &&
        this.props.settings.images &&
        this.props.settings.images.banner;
    }

    if (!this.props.isKioskMode) {
      iconElementRight = <AccountAppBarItem compact={!isLarge} />;
    } else if (!this.context.router.isActive("/schedule")) {
      iconElementRight = (
        <Link to="/schedule">
          <FlatButton style={{ marginTop: 3 }} label={"Return to schedule"} />
        </Link>
      );
    }

    const MenuButtonComponent = this.state.tenant.invert_colors_alt
      ? RaisedButton
      : FlatButton;

    const name =
      this.state.tenant.display_name ||
      this.state.tenant.display_short_name ||
      (this.props.settings && this.props.settings.name);

    let includeHeader = (this.props.location.query.mobile === "true") ? false : true;
    // For now, gate this specifically to Sundace
    if (this.state.tenant && this.state.tenant.sundance_login) {
      try {
        // if (query param mobile=true is set, add to localStorage)
        if (!includeHeader) {
          window.localStorage["eventive-hideHeader"] = true;
        }
        // if localStorage has hideHeader set to true, we should not include the header
        else {
          if (window.localStorage["eventive-hideHeader"]) { 
            includeHeader = false;
          }
        }
      } catch (e) {
        // Catch if unable to access localStorage (e.g. due to DOM Exception 18)
        console.log("Error attempting to use localStorage");
      }
    }

    return (
      <_StripeProvider apiKey={process.env.STRIPE_PUBLIC_KEY}>
        <MuiThemeProvider muiTheme={muiTheme}>
          <StyleRoot>
            <div
              style={styles.canvas}
              className={isApp() ? "eventive-app" : null}
            >
              {/* Used for live previews of edits to tenant config */}
              <AdminCommunicator
                onUpdateTenant={this.updateTenant}
                onOpenLeftNav={() => this.refs.leftNav.open()}
                onCloseLeftNav={() => this.refs.leftNav.close()}
                router={this.context.router}
              />

              <Helmet
                title={this.state.tenant.display_name}
                titleTemplate={`%s | ${name}`}
                meta={[
                  //{ name: "og:title", content: name },
                  { name: "fb:app_id", content: config.facebook_app_id },
                  { name: "og:url", content: `${window.location.origin}${this.props.router.location.pathname}`},
                  //{ name: "og:image", content: this.state.tenant.social_media_image_url }
                ]}
              />

              <Dialog
                open={
                  this.props.user &&
                  !this.props.user.loginInProgress &&
                  this.props.user.token &&
                  !this.props.user.isLoggedIn
                    ? true
                    : false
                }
                className="Eventive--Dialog Eventive--Dialog--Already_Logged_In"
                modal={true}
              >
                <div style={styles.dialog.logoContainer}>
                  <img
                    style={styles.dialog.logo}
                    src="/img/eventive-dark.png"
                  />
                </div>
                <div>
                  <p>
                    Welcome to {name}! You're already logged in to an Eventive
                    account via another site, so we've automatically logged you
                    into this site as well.
                  </p>
                  <div style={styles.dialog.actions}>
                    <RaisedButton
                      label="Continue to site"
                      primary={true}
                      style={styles.dialog.primaryAction}
                      onClick={() => PersonActions.loginSucceeded()}
                    />
                    <FlatButton
                      label="Log out & continue"
                      primary={true}
                      style={styles.dialog.secondaryAction}
                      onClick={() => PersonActions.logout()}
                    />
                  </div>
                </div>
              </Dialog>

              <Dialog open={this.state.showDownloadAppModal} 
                modal={true}
                className="Eventive--Dialog Eventive--Dialog--Show_Download_App"
              >
                <div style={styles.dialog.logoContainer}>
                  <a
                    href={
                      checkDevice().isAndroid
                        ? this.state.tenant.google_play_store_url
                        : this.state.tenant.apple_app_store_url
                    }
                  >
                    <img
                      style={{ width: 200 }}
                      src={
                        checkDevice().isAndroid
                          ? "/img/google-play-badge.png"
                          : "/img/apple-app-store-badge.svg"
                      }
                    />
                  </a>
                </div>
                <div>
                  <p>
                    Get the App! Download the App for{" "}
                    {checkDevice().isAndroid ? "Android" : "iPhone and iPad"}{" "}
                    now by clicking on the image above. Or if you'd prefer to
                    use your browser, you can...
                  </p>
                  <div style={styles.dialog.actions}>
                    <RaisedButton
                      label="Continue to site"
                      primary={true}
                      style={styles.dialog.primaryAction}
                      onClick={() => {
                        this.setState({ showDownloadAppModal: false });
                      }}
                    />
                  </div>
                </div>
              </Dialog>

              {this.props.fetchFailed ? (
                <Snackbar
                  message="Unable to connect to Eventive backend. The site will not function properly."
                  action="refresh"
                  openOnMount={true}
                  onActionClick={() => {
                    window.location.reload();
                  }}
                />
              ) : null}

              <Snackbar
                autoHideDuration={5000}
                message={this.props.globalNotification.message}
                open={this.props.globalNotification.open}
                action={
                  this.props.globalNotification.action &&
                  this.props.globalNotification.action.label
                }
                onActionClick={
                  this.props.globalNotification.action &&
                  this.props.globalNotification.action.onClick
                }
                onRequestClose={() => GlobalNotificationActions.close()}
              />

              {SWITCHER_ORGS.includes(this.state.tenant.owner) ? (
                <div id="event_bucket_switcher" style={styles.switcher}>
                  <div style={{ paddingRight: 10, width: 260, float: "right" }}>
                    <Select
                      name="test"
                      style={{ zIndex: 5000, width: 250, height: 20 }}
                      value={this.state.selectedEventBucket}
                      options={this.state.eventBucketOptions}
                      menuContainerStyle={{ zIndex: 5000, width: 250 }}
                      menuStyle={{ width: 250 }}
                      clearable={false}
                      onChange={this.switchEventBucket}
                    />
                  </div>
                </div>
              ) : null}


              {includeHeader ? (
                <AppBar
                  onLeftIconButtonClick={this._onLeftIconButtonClick}
                  title={
                    <Link
                      className="displayFlex"
                      style={{ color: "inherit", textDecoration: "none" }}
                      to={this.props.isKioskMode ? "/schedule" : "/welcome"}
                    >
                      {this.state.tenant.logo_image ? (
                        <img
                          style={styles.titleImage}
                          src={this.state.tenant.logo_image}
                        />
                      ) : (
                        this.state.tenant.display_short_name
                      )}
                    </Link>
                  }
                  titleStyle={styles.title}
                  iconStyleRight={{
                    marginTop: 10,
                    marginLeft: 0,
                    marginRight: 0
                  }}
                  iconStyleLeft={{ marginLeft: -16 }}
                  showMenuIconButton={!this.props.isKioskMode}
                  iconElementRight={iconElementRight}
                  iconElementLeft={
                    isLarge ? (
                      <MenuButtonComponent
                        style={{
                          marginTop: 5,
                          fontWeight: 700,
                          fontSize: "14px",
                          textTransform: "uppercase",
                          marginLeft: 16,
                          marginRight: 6,
                          minWidth: 60
                        }}
                        buttonStyle={{
                          color: this.state.tenant.invert_colors_alt
                            ? "#fff"
                            : undefined
                        }}
                        onClick={this._onLeftIconButtonClick}
                        primary={this.state.tenant.invert_colors_alt}
                      >
                        MENU
                      </MenuButtonComponent>
                    ) : (
                      <IconButton onClick={this._onLeftIconButtonClick}>
                        <NavigationMenu
                          color={
                            this.state.tenant.invert_colors_alt
                              ? "#000"
                              : undefined
                          }
                        />
                      </IconButton>
                    )
                  }
                  style={styles.bar}
                  zDepth={0}
                />
              ) : null}

              {!this.props.isKioskMode ? (
                <AppLeftNav
                  ref="leftNav"
                  router={this.context.router}
                  docked={false}
                />
              ) : null}

              <div style={includeHeader ? styles.wrapper : styles.wrapperNoHeader}>
                {isLarge ? (
                  <div style={styles.bannerWrapper}>
                    <Link
                      to={
                        this.props.isKioskMode
                          ? "/schedule"
                          : path.some(p => p.path.indexOf("schedule/") !== -1)
                          ? "/schedule"
                          : "/welcome"
                      }
                    >
                      <Banner />
                    </Link>
                  </div>
                ) : null}
                <div style={styles.content}>
                  {this.props.fetchSuccessful ? (
                    React.cloneElement(this.props.children, {
                      appSettings: this.props.settings || {},
                      previewMode: this.props.isPreviewMode
                    })
                  ) : (
                    <div style={{ padding: 10, textAlign: "center" }}>
                      <CircularProgress
                        mode="indeterminate"
                        size={119}
                        thickness={7}
                        style={{ margin: 10.5 }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <FullWidthSection style={styles.footer} className="Eventive--Footer">
                <div>
                  <div style={styles.poweredBy}>
                    <a
                      style={styles.footerLink}
                      href="https://eventive.org"
                      target="_blank"
                    >
                      Powered by{" "}
                      <img
                        style={styles.eventiveLogo}
                        src="/img/eventive.png"
                      />
                    </a>
                  </div>
                  {/*<div>
                Local time: {moment().format('LT (z)')}
              </div>*/}
                  <div>
                    <a
                      style={styles.footerLink}
                      href="https://eventive.org/terms"
                      target="_blank"
                    >
                      Terms
                    </a>{" "}
                    ·{" "}
                    <a
                      style={styles.footerLink}
                      href="https://eventive.org/privacy"
                      target="_blank"
                    >
                      Privacy
                    </a>{" "}
                    ·{" "}
                    <a
                      style={styles.footerLink}
                      href="https://status.eventive.org/"
                      target="_blank"
                    >
                      System Status
                    </a>
                  </div>
                </div>
              </FullWidthSection>

              {this.state.tenant.additional_scripts ? (
                <InjectedTenantScripts
                  content={this.state.tenant.additional_scripts}
                />
              ) : null}
            </div>
          </StyleRoot>
        </MuiThemeProvider>
      </_StripeProvider>
    );
  }
}

App.displayName = "App";

export default withWidth()(App);



// WEBPACK FOOTER //
// ./src/app/components/app.jsx